import React, { useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Plus, Trash2, Link2, BookOpen, X, CalendarDays, Clock, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCollection } from "@/hooks/useCollection";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const SUBJECT_COLORS = ["#8FA9E3", "#6FC2B0", "#EE9C86", "#B69CE0", "#78B7E0"];

export default function Planner() {
  const { items, loading, create, update, remove } = useCollection("lesson-plans");
  const events = useCollection("events");
  const [tab, setTab] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank("daily"));
  const [linkDraft, setLinkDraft] = useState({ title: "", url: "" });

  function blank(type) {
    return { title: "", plan_type: type, date: new Date().toISOString().slice(0, 10), day_of_week: "Monday", subject: "", class_name: "", objectives: "", activities: "", notes: "", resources: [] };
  }

  const openNew = () => { setEditing(null); setForm({ ...blank(tab), date: tab === "daily" ? selectedDate : new Date().toISOString().slice(0, 10) }); setLinkDraft({ title: "", url: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, resources: p.resources || [] }); setLinkDraft({ title: "", url: "" }); setOpen(true); };

  const addLink = () => {
    if (!linkDraft.title.trim() || !linkDraft.url.trim()) return;
    setForm({ ...form, resources: [...(form.resources || []), { ...linkDraft }] });
    setLinkDraft({ title: "", url: "" });
  };
  const removeLink = (i) => setForm({ ...form, resources: form.resources.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a lesson title");
    if (editing) { await update(editing.id, form); toast.success("Plan updated"); }
    else { await create(form); toast.success("Plan created"); }
    setOpen(false);
  };

  const filtered = items.filter((p) => (p.plan_type || "daily") === tab);
  const dayPlans = items.filter((p) => (p.plan_type || "daily") === "daily" && p.date === selectedDate);
  const dayEvents = events.items
    .filter((e) => e.date === selectedDate)
    .sort((a, b) => {
      if (!a.start_time) return 1;
      if (!b.start_time) return -1;
      return a.start_time.localeCompare(b.start_time);
    });

  const shiftDate = (dir) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + dir);
    setSelectedDate(d.toISOString().slice(0, 10));
  };
  const prettyDate = new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div>
      <PageHeader
        title="Lesson Planner" icon={NotebookPen}
        subtitle="Your plans and your real schedule, side by side."
        action={<Button onClick={openNew} data-testid="add-plan-button" className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> New plan</Button>}
      />

      <div className="flex bg-slate-100 rounded-full p-1 w-fit mb-6">
        {["daily", "weekly"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`planner-tab-${t}`}
            className={`px-6 py-2 rounded-full text-sm font-medium capitalize transition-colors ${tab === t ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "daily" ? (
        <div>
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Button variant="outline" size="icon" onClick={() => shiftDate(-1)} className="rounded-full" data-testid="planner-prev-day"><ChevronLeft size={18} /></Button>
            <span className="font-heading font-bold text-slate-800 min-w-[220px] text-center">{prettyDate}</span>
            <Button variant="outline" size="icon" onClick={() => shiftDate(1)} className="rounded-full" data-testid="planner-next-day"><ChevronRight size={18} /></Button>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="planner-date-picker"
              className="ml-2 rounded-xl border border-slate-200 bg-white h-9 px-3 text-sm" />
            <Button variant="ghost" onClick={() => setSelectedDate(new Date().toISOString().slice(0, 10))} className="rounded-full text-primary text-sm" data-testid="planner-today">Today</Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Lesson plans column */}
            <div data-testid="planner-plans-column">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-xl bg-primary/12 flex items-center justify-center"><BookOpen size={18} className="text-primary" strokeWidth={1.7} /></div>
                <h2 className="font-heading font-bold text-slate-800">Lesson Plans</h2>
                <span className="text-xs text-slate-400">({dayPlans.length})</span>
              </div>
              {dayPlans.length === 0 ? (
                <div className="tcc-card p-8 text-center">
                  <p className="text-slate-400 text-sm mb-4">No lesson plans for this day yet.</p>
                  <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid="planner-add-for-day"><Plus size={16} className="mr-1" /> Plan a lesson</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {dayPlans.map((p, i) => {
                    const color = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
                    return (
                      <button key={p.id} onClick={() => openEdit(p)} data-testid="plan-card"
                        className="tcc-card p-5 text-left w-full hover:-translate-y-0.5 transition-transform duration-200 group">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${color}22`, color }}>{p.subject || "Lesson"}</span>
                          <span onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-destructive transition"><Trash2 size={15} /></span>
                        </div>
                        <h3 className="font-heading font-bold text-slate-800 mb-1">{p.title}</h3>
                        {p.class_name && <p className="text-xs text-slate-400 mb-2">{p.class_name}</p>}
                        {p.objectives && <p className="text-sm text-slate-600 line-clamp-2">{p.objectives}</p>}
                        {(p.resources?.length > 0) && <div className="flex items-center gap-1.5 mt-3 text-xs text-primary"><Link2 size={13} /> {p.resources.length} resource{p.resources.length > 1 ? "s" : ""}</div>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Schedule column */}
            <div data-testid="planner-schedule-column">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-xl bg-secondary/40 flex items-center justify-center"><CalendarDays size={18} className="text-slate-700" strokeWidth={1.7} /></div>
                <h2 className="font-heading font-bold text-slate-800">Your Schedule</h2>
                <span className="text-xs text-slate-400">({dayEvents.length})</span>
              </div>
              <div className="tcc-card p-5">
                {dayEvents.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Nothing on the calendar for this day.</p>
                ) : (
                  <div className="space-y-3">
                    {dayEvents.map((e) => (
                      <div key={e.id} className="flex gap-3" data-testid="planner-schedule-event">
                        <div className="w-16 shrink-0 text-right">
                          <p className="text-sm font-semibold text-slate-700">{e.start_time || "—"}</p>
                          {e.end_time && <p className="text-xs text-slate-400">{e.end_time}</p>}
                        </div>
                        <div className="w-1 rounded-full shrink-0" style={{ background: e.color || "#8FA9E3" }} />
                        <div className="flex-1 pb-1">
                          <p className="font-semibold text-slate-800">{e.title}</p>
                          {e.location && <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} />{e.location}</p>}
                          {e.notes && <p className="text-xs text-slate-500 mt-0.5">{e.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : loading ? <p className="text-slate-400">Loading…</p> : filtered.length === 0 ? (
        <div className="tcc-card p-12 text-center">
          <BookOpen className="mx-auto text-primary/50 mb-3" size={40} strokeWidth={1.4} />
          <p className="font-heading font-bold text-slate-700 text-lg">No weekly plans yet</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Create your first weekly lesson plan and reuse the structure all year.</p>
          <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90"><Plus size={18} className="mr-1" /> Create plan</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p, i) => {
            const color = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
            return (
              <button key={p.id} onClick={() => openEdit(p)} data-testid="plan-card"
                className="tcc-card p-5 text-left hover:-translate-y-1 transition-transform duration-200 group">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${color}22`, color }}>
                    {p.subject || "Lesson"}
                  </span>
                  <span onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-destructive transition"><Trash2 size={15} /></span>
                </div>
                <h3 className="font-heading font-bold text-slate-800 mb-1">{p.title}</h3>
                <p className="text-xs text-slate-400 mb-3">{[p.class_name, p.day_of_week].filter(Boolean).join(" · ")}</p>
                {p.objectives && <p className="text-sm text-slate-600 line-clamp-2">{p.objectives}</p>}
                {(p.resources?.length > 0) && (
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-primary"><Link2 size={13} /> {p.resources.length} resource{p.resources.length > 1 ? "s" : ""}</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit lesson plan" : "New lesson plan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Lesson title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="plan-title-input" className="rounded-xl mt-1" placeholder="Intro to Fractions" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="plan-subject-input" className="rounded-xl mt-1" placeholder="Math" /></div>
              <div><Label>Class / Period</Label><Input value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} className="rounded-xl mt-1" placeholder="Period 3" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.plan_type === "daily" ? (
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1" /></div>
              ) : (
                <div>
                  <Label>Day</Label>
                  <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Plan type</Label>
                <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Objectives</Label><Textarea value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} className="rounded-xl mt-1" rows={2} placeholder="Students will be able to…" /></div>
            <div><Label>Activities</Label><Textarea value={form.activities} onChange={(e) => setForm({ ...form, activities: e.target.value })} className="rounded-xl mt-1" rows={3} placeholder="Warm-up, direct instruction, practice…" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" rows={2} /></div>

            <div>
              <Label>Attached resources</Label>
              <div className="space-y-1.5 mt-1.5">
                {(form.resources || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                    <Link2 size={14} className="text-primary shrink-0" />
                    <span className="text-sm text-slate-700 flex-1 truncate">{r.title}</span>
                    <button onClick={() => removeLink(i)} className="text-slate-400 hover:text-destructive"><X size={14} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={linkDraft.title} onChange={(e) => setLinkDraft({ ...linkDraft, title: e.target.value })} placeholder="Label" className="rounded-xl h-9" data-testid="plan-link-title" />
                  <Input value={linkDraft.url} onChange={(e) => setLinkDraft({ ...linkDraft, url: e.target.value })} placeholder="https://…" className="rounded-xl h-9" data-testid="plan-link-url" />
                  <Button type="button" variant="outline" onClick={addLink} className="rounded-xl h-9 shrink-0" data-testid="plan-link-add"><Plus size={16} /></Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="plan-save-button" className="rounded-full bg-primary hover:bg-primary/90">{editing ? "Save plan" : "Create plan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
